import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth-context';
import { usePWA } from '@/context/pwa-context';
import { usePayment } from '@/contexts/PaymentContext';
import { apiFetch } from '@/lib/api';
import { User, Save, LogOut, Shield, Lock, Eye, EyeOff, Download, Smartphone, Monitor, CreditCard, ArrowUpCircle, XCircle, Zap, ShieldCheck, History, ExternalLink, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Navigate } from 'react-router-dom';
import { PasswordResetFlow } from '@/components/settings/password-reset-flow';
import { CloudStorageSection } from '@/components/settings/CloudStorageSection';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const Settings: React.FC = () => {
  const { user, loading: authLoading, updateUsername, changePassword, logout, refreshUser } = useAuth();
  const { isInstallable, installApp } = usePWA();
  const { subscription, loading: paymentLoading, isPro, isElite, isEnterprise, refreshSubscription } = usePayment();
  const [username, setUsername] = useState(user?.username || '');
  const [saveLoading, setSaveLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [purchaseHistory, setPurchaseHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [mfaData, setMfaData] = useState<{ enabled: boolean; enabledAt?: number } | null>(null);
  const [mfaSetupData, setMfaSetupData] = useState<{ manualSetupCode: string; qrCodeUrl: string; manualEntryLabel?: string; issuer?: string } | null>(null);
  const [mfaSetupStep, setMfaSetupStep] = useState<'idle' | 'setup' | 'verify' | 'success' | 'rotate_verify' | 'disable_verify'>('idle');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [mfaLoading, setMfaLoading] = useState(false);

  useEffect(() => {
    const fetchMfaStatus = async () => {
      try {
        await refreshUser();
      } catch (e) {}
    };
    fetchMfaStatus();
  }, []);

  const startMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const res = await apiFetch('/api/auth/mfa-setup', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        setMfaSetupData(data);
        setMfaSetupStep('setup');
      } else {
        toast.error(data.error || 'Failed to start MFA setup');
      }
    } catch (e) {
      toast.error('Failed to connect to server');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (!verificationCode || !mfaSetupData) return;
    setMfaLoading(true);
    try {
      const res = await apiFetch('/api/auth/mfa-verify', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verificationCode })
      });
      const data = await res.json();
      if (res.ok) {
        setBackupCodes(data.backupCodes);
        setMfaSetupStep('success');
        toast.success('MFA enabled successfully!');
        // Refresh to update system state, but keep local backup codes visible
        await refreshUser();
      } else {
        toast.error(data.error || 'Verification failed');
      }
    } catch (e) {
      toast.error('Failed to connect to server');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaDisable = async () => {
    if (!verificationCode) return;
    setMfaLoading(true);
    try {
      const res = await apiFetch('/api/auth/mfa-setup', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verificationCode })
      });
      if (res.ok) {
        toast.success('Two-factor authentication disabled.');
        setMfaSetupStep('idle');
        setVerificationCode('');
        await refreshUser();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to disable MFA');
      }
    } catch (e) {
      toast.error('Failed to connect to server');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaRotate = async () => {
    if (!verificationCode) return;
    setMfaLoading(true);
    try {
      const res = await apiFetch('/api/auth/mfa-regenerate-backup-codes', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verificationCode })
      });
      const data = await res.json();
      if (res.ok) {
        setBackupCodes(data.backupCodes);
        setMfaSetupStep('success');
        setVerificationCode('');
        toast.success('New backup codes generated!');
      } else {
        toast.error(data.error || 'Failed to rotate codes');
      }
    } catch (e) {
      toast.error('Failed to connect to server');
    } finally {
      setMfaLoading(false);
    }
  };

  const [passwordState, setPasswordState] = useState({
    current: '', new: '', confirm: '', showCurrent: false, showNew: false, showConfirm: false, loading: false, message: ''
  });

  const [resetStep, setResetStep] = useState<'none' | 'active'>('none');

  useEffect(() => { if (user?.username) setUsername(user.username); }, [user]);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will keep your access until the end of the billing period.')) return;
    setCancelLoading(true);
    try {
      const res = await apiFetch('/api/payments/cancel', { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Subscription scheduled for cancellation. Access remains until ${subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'end of period'}`);
        await refreshSubscription();
      } else {
        toast.error(data.error || 'Failed to cancel subscription');
      }
    } catch (e) {
      toast.error('Failed to connect to server');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivateLoading(true);
    try {
      const res = await apiFetch('/api/payments/reactivate', { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success('Subscription reactivated! Your automatic billing will continue on the next billing date.');
        await refreshSubscription();
      } else {
        toast.error(data.error || 'Failed to reactivate subscription');
      }
    } catch (e) {
      toast.error('Failed to connect to server');
    } finally {
      setReactivateLoading(false);
    }
  };

  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) return;
      setHistoryLoading(true);
      try {
        const res = await apiFetch('/api/payments/history');
        if (res.ok) {
          const data = await res.json();
          setPurchaseHistory(data);
        }
      } catch (e) {
        console.error('History fetch failed:', e);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [user]);

  if (authLoading || paymentLoading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div></div>;
  if (!user) return <Navigate to="/login" />;

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveLoading(true); setMessage('');
    try { await updateUsername(username); setMessage('Username updated successfully!'); }
    catch (err: any) { setMessage('Error: ' + err.message); }
    finally { setSaveLoading(false); }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordState(prev => ({ ...prev, message: '' }));
    if (passwordState.new !== passwordState.confirm) return setPasswordState(prev => ({ ...prev, message: 'Error: New passwords do not match' }));
    if (passwordState.new.length < 6) return setPasswordState(prev => ({ ...prev, message: 'Error: Password must be at least 6 characters' }));
    setPasswordState(prev => ({ ...prev, loading: true }));
    try {
      await changePassword(passwordState.current, passwordState.new);
      setPasswordState(prev => ({ ...prev, message: 'Password updated successfully!', current: '', new: '', confirm: '' }));
    } catch (err: any) {
      setPasswordState(prev => ({ ...prev, message: 'Error: ' + err.message }));
    } finally {
      setPasswordState(prev => ({ ...prev, loading: false }));
    }
  };

  const currentPlanId = (user?.plan || 'FREE').toLowerCase();
  const isBeta = user?.is_beta;
  const isTrial = user?.is_trial;
  const accessRemaining = user?.access_remaining || 0;

  const planInfoBase = {
    free: { name: 'Starter Studio', price: 'Free', color: 'bg-zinc-100 text-zinc-500' },
    creator_pro: { name: 'Pro Creator', price: '$49 / Month', color: 'bg-cyan-500 text-white' },
    studio_elite: { name: 'Studio Elite', price: '$199 / Month', color: 'bg-[#ffea00] text-black shadow-[0_0_15px_rgba(255,234,0,0.3)]' },
    enterprise: { name: 'Enterprise Studio', price: 'Custom Pricing', color: 'bg-zinc-900 text-white' }
  };

  const planInfo = (isBeta ? { name: 'Beta Tester', price: 'Free (Beta Access)', color: 'bg-emerald-500 text-white' } : (planInfoBase as any)[currentPlanId]) || { 
    name: currentPlanId.charAt(0).toUpperCase() + currentPlanId.slice(1).replace('_', ' '), 
    price: 'Custom Plan', 
    color: 'bg-zinc-100 text-zinc-500' 
  };

  return (
    <div className="min-h-screen pb-20 relative overflow-hidden font-sans bg-[#fcfdff]">
      <SEO title="Settings" noindex />
      <div className="absolute inset-x-0 top-0 h-[500px] bg-gradient-to-b from-[#f0f7ff] to-transparent -z-10" />
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(6,182,212,0.06)_0%,transparent_50%)]" />
      </div>

      <div className="max-w-2xl mx-auto px-6 pt-24 relative z-10">
        <div className="mb-12">
          <Badge variant="outline" className="mb-4 font-mono text-[9px] tracking-[0.4em] uppercase px-3 py-1 border-cyan-500/10 text-cyan-600 bg-cyan-500/5 rounded-full">Settings</Badge>
          <h1 className="text-4xl md:text-5xl font-black font-display text-cyan-950 tracking-tighter uppercase leading-[0.9]">
            Account <span className="text-cyan-600/40">Portal</span>
          </h1>
        </div>

        <div className="space-y-12">
          {/* Profile Section */}
          <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
            <div className="flex flex-col md:flex-row items-center gap-10 mb-12">
              <div className="w-24 h-24 rounded-[2rem] bg-zinc-50 border border-cyan-500/10 flex items-center justify-center shadow-xl shadow-cyan-500/5 overflow-hidden">
                {user.picture ? (
                   <img src={user.picture} alt={user.username} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                   <User className="w-10 h-10 text-zinc-300" />
                )}
              </div>
              <div className="text-center md:text-left">
                <h2 className="text-3xl font-bold font-display text-zinc-900 uppercase tracking-tighter">{user.username}</h2>
                <p className="text-cyan-600 font-mono text-xs mt-1">{user.email}</p>
              </div>
            </div>

            <form onSubmit={handleUpdate} className="space-y-8">
              <div className="space-y-3">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400">Display Name</label>
                  {user.needs_username_setup === 0 && (
                    <span className="text-[9px] font-bold text-cyan-600/50 uppercase tracking-widest flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                <div className="relative group/input">
                  <User className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 group-focus-within/input:text-cyan-500 transition-colors" />
                  <input 
                    type="text" 
                    value={username} 
                    onChange={(e) => setUsername(e.target.value)} 
                    disabled={user.needs_username_setup === 0}
                    className={`block w-full pl-14 pr-6 py-5 bg-zinc-50 border border-cyan-500/5 rounded-2xl text-zinc-900 focus:outline-none focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500/20 transition-all font-bold placeholder:text-zinc-300 shadow-sm ${user.needs_username_setup === 0 ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed border-transparent shadow-none' : ''}`} 
                  />
                </div>
                {user.needs_username_setup === 0 && (
                  <p className="text-[10px] text-zinc-400 font-medium px-1">
                    Username can only be set once and is now permanent.
                  </p>
                )}
              </div>
              {message && (
                <div className={`p-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-center animate-in fade-in slide-in-from-top-2 ${
                  message.startsWith('Error') ? 'bg-red-50 text-red-500 border border-red-100' : 'bg-green-50 text-green-500 border border-green-100'
                }`}>
                  {message}
                </div>
              )}
              {user.needs_username_setup !== 0 && (
                <button 
                  type="submit" 
                  disabled={saveLoading} 
                  className="w-full h-16 bg-cyan-600 text-white font-black text-xs rounded-2xl hover:bg-cyan-500 transition-all shadow-xl shadow-cyan-500/10 uppercase tracking-[0.3em] flex items-center justify-center gap-3"
                >
                  {saveLoading ? 'Processing...' : (
                    <>
                      <Save className="w-4 h-4" /> Save Changes
                    </>
                  )}
                </button>
              )}
            </form>
          </section>

          {/* Security Section */}
          {user.has_password === false ? (
              <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-cyan-600 rounded-2xl flex items-center justify-center border border-cyan-500/10 shadow-xl shadow-cyan-500/10">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold font-display text-zinc-900 uppercase tracking-tighter">Security</h3>
                </div>
                <div className="space-y-6">
                  <div className="p-6 bg-zinc-50 rounded-3xl border border-zinc-100 mb-6">
                    <div className="flex items-center gap-3 mb-2">
                       <Globe className="w-4 h-4 text-cyan-600" />
                       <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Google Connected</h4>
                    </div>
                    <p className="text-zinc-500 text-xs font-mono uppercase tracking-[0.15em] leading-relaxed">
                      You are logged in with Google. You can set a password to enable traditional login too.
                    </p>
                  </div>
                  
                  <button 
                    onClick={() => setResetStep('active')}
                    className="w-full h-16 bg-cyan-600 text-white font-black text-xs rounded-2xl hover:bg-cyan-500 transition-all shadow-xl shadow-cyan-500/10 uppercase tracking-[0.3em] flex items-center justify-center gap-3"
                  >
                    <Lock className="w-4 h-4" /> Setup Password Login
                  </button>
                </div>
              </section>
          ) : (
            <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
              <div className="flex items-center gap-4 mb-10">
                <div className="w-12 h-12 bg-cyan-600 rounded-2xl flex items-center justify-center border border-cyan-500/10 shadow-xl shadow-cyan-500/10">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold font-display text-zinc-900 uppercase tracking-tighter">Password & Security</h3>
              </div>

              <form onSubmit={handlePasswordChange} className="space-y-6">
                <PasswordField label="Current Password" value={passwordState.current} show={passwordState.showCurrent} onChange={v => setPasswordState(p => ({ ...p, current: v }))} toggleShow={() => setPasswordState(p => ({ ...p, showCurrent: !p.showCurrent }))} />
                
                <div className="flex justify-end pr-1">
                  <button type="button" onClick={() => setResetStep('active')} className="text-[10px] font-black text-cyan-600 hover:text-cyan-500 transition-colors uppercase tracking-[0.2em]">Reset via email</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <PasswordField label="New Password" value={passwordState.new} show={passwordState.showNew} onChange={v => setPasswordState(p => ({ ...p, new: v }))} toggleShow={() => setPasswordState(p => ({ ...p, showNew: !p.showNew }))} />
                  <PasswordField label="Confirm New Password" value={passwordState.confirm} show={passwordState.showConfirm} onChange={v => setPasswordState(p => ({ ...p, confirm: v }))} toggleShow={() => setPasswordState(p => ({ ...p, showConfirm: !p.showConfirm }))} />
                </div>

                {passwordState.message && (
                  <div className={`p-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-center animate-in fade-in slide-in-from-top-2 ${
                    passwordState.message.startsWith('Error') ? 'bg-red-50 text-red-500 border border-red-100' : 'bg-cyan-50 text-cyan-600 border border-cyan-100'
                  }`}>
                    {passwordState.message}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={passwordState.loading} 
                  className="w-full h-16 border border-cyan-500/10 text-cyan-600 font-black text-xs rounded-2xl hover:bg-cyan-600 hover:text-white transition-all uppercase tracking-[0.2em]"
                >
                  {passwordState.loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </section>
          )}

          {resetStep === 'active' && <PasswordResetFlow email={user.email} onSuccess={async () => { setResetStep('none'); await refreshUser(); }} onCancel={() => setResetStep('none')} />}

          {/* MFA Section */}
          <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 bg-cyan-600 rounded-2xl flex items-center justify-center border border-cyan-500/10 shadow-xl shadow-cyan-500/10">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold font-display text-zinc-900 uppercase tracking-tighter">Two-Factor Authentication</h3>
            </div>

            {mfaSetupStep === 'idle' && (
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-zinc-50 rounded-3xl border border-zinc-100">
                  <div className="shrink-0 mt-1">
                    <Zap className={`w-5 h-5 ${user.mfa_enabled ? 'text-cyan-500' : 'text-zinc-300'}`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-black uppercase tracking-widest text-zinc-900 mb-1">
                      Status: {user.mfa_enabled ? 'Enabled' : 'Disabled'}
                    </h4>
                    <p className="text-[11px] text-zinc-500 font-medium leading-relaxed uppercase tracking-tight">
                      {user.mfa_enabled 
                        ? 'Your account is protected with an additional layer of security.' 
                        : 'Add an extra layer of security to your account by requiring a code from your phone to log in.'}
                    </p>
                  </div>
                </div>

                {!user.mfa_enabled ? (
                  <button 
                    onClick={startMfaSetup}
                    disabled={mfaLoading}
                    className="w-full h-16 bg-cyan-600 text-white font-black text-xs rounded-2xl hover:bg-cyan-500 transition-all uppercase tracking-[0.3em] flex items-center justify-center gap-2 shadow-xl shadow-cyan-500/10"
                  >
                    {mfaLoading ? 'Loading...' : 'Enable Two-Factor Auth'}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-cyan-50 border border-cyan-100 rounded-2xl text-center">
                      <p className="text-[10px] font-black uppercase tracking-widest text-cyan-600">
                        Two-factor authentication is active.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <button 
                        onClick={() => setMfaSetupStep('rotate_verify')}
                        disabled={mfaLoading}
                        className="h-12 border border-cyan-100 text-cyan-600 font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-cyan-50 transition-all font-bold"
                      >
                        Rotate Codes
                      </button>
                      <button 
                        onClick={() => setMfaSetupStep('disable_verify')}
                        disabled={mfaLoading}
                        className="h-12 border border-red-100 text-red-500 font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-red-50 transition-all font-bold"
                      >
                        Disable
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {(mfaSetupStep === 'rotate_verify' || mfaSetupStep === 'disable_verify') && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="space-y-4">
                  <div className="space-y-2 text-center">
                    <h4 className="text-sm font-black uppercase tracking-widest text-zinc-900">
                      {mfaSetupStep === 'rotate_verify' ? 'Verify Rotation' : 'Verify Disable'}
                    </h4>
                    <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Enter Verification Code</p>
                    <input 
                      type="text" 
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                      className="w-full max-w-[200px] mx-auto block h-16 text-center text-2xl font-mono font-bold tracking-[0.5em] bg-zinc-50 border border-cyan-100 rounded-2xl focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500"
                      placeholder="000000"
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-4">
                    <button 
                      onClick={() => {
                        setMfaSetupStep('idle');
                        setVerificationCode('');
                      }}
                      className="flex-1 h-16 border border-zinc-100 text-zinc-400 font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-zinc-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={mfaSetupStep === 'rotate_verify' ? handleMfaRotate : handleMfaDisable}
                      disabled={verificationCode.length !== 6 || mfaLoading}
                      className={`flex-[2] h-16 font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all shadow-xl disabled:opacity-50 ${
                        mfaSetupStep === 'rotate_verify' 
                          ? 'bg-cyan-600 text-white hover:bg-cyan-500 shadow-cyan-500/20' 
                          : 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20'
                      }`}
                    >
                      {mfaLoading ? 'Verifying...' : (mfaSetupStep === 'rotate_verify' ? 'Rotate Codes' : 'Disable MFA')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {mfaSetupStep === 'setup' && mfaSetupData && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="text-center space-y-4">
                  <div className="bg-white p-4 inline-block rounded-3xl border border-cyan-100 shadow-xl shadow-cyan-500/5">
                    <img src={mfaSetupData.qrCodeUrl} alt="QR Code" className="w-40 h-40" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Step 1: Scan QR Code</p>
                    {mfaSetupData.manualSetupCode && (
                       <div className="mb-4">
                         <p className="text-[8px] font-bold text-zinc-300 uppercase tracking-widest mb-1">Manual Entry Code:</p>
                         <code className="text-[10px] font-mono bg-zinc-50 border border-zinc-100 p-2 rounded block select-all">{mfaSetupData.manualSetupCode}</code>
                       </div>
                    )}
                    <p className="text-xs font-medium text-zinc-600 max-w-xs mx-auto">
                      Open your authenticator app (e.g. Google Authenticator) and scan this code.
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-zinc-100">
                  <div className="space-y-2 text-center">
                    <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Step 2: Enter Verification Code</p>
                    <input 
                      type="text" 
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                      className="w-full max-w-[200px] mx-auto block h-16 text-center text-2xl font-mono font-bold tracking-[0.5em] bg-zinc-50 border border-cyan-100 rounded-2xl focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500"
                      placeholder="000000"
                    />
                  </div>

                  <div className="flex gap-4">
                    <button 
                      onClick={() => setMfaSetupStep('idle')}
                      className="flex-1 h-16 border border-zinc-100 text-zinc-400 font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-zinc-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleMfaVerify}
                      disabled={verificationCode.length !== 6 || mfaLoading}
                      className="flex-[2] h-16 bg-cyan-600 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-cyan-500 transition-all shadow-xl shadow-cyan-500/20 disabled:opacity-50"
                    >
                      {mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {mfaSetupStep === 'success' && (
              <div className="space-y-8 animate-in zoom-in duration-500">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto text-white shadow-xl shadow-green-500/20">
                    <ShieldCheck className="w-8 h-8" />
                  </div>
                  <h4 className="text-xl font-bold uppercase tracking-tighter text-zinc-900">MFA is Now Enabled!</h4>
                  <div className="p-6 bg-amber-50 border border-amber-100 rounded-[2rem] text-left">
                    <p className="text-[11px] font-black uppercase tracking-widest text-amber-800 mb-4 flex items-center gap-2">
                       Save Your Backup Codes
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {backupCodes.map((code, i) => (
                        <code key={i} className="bg-white p-3 rounded-xl border border-amber-200 font-mono text-xs text-center font-bold tracking-widest text-amber-900 shadow-sm">
                          {code}
                        </code>
                      ))}
                    </div>
                    <p className="text-[10px] text-amber-700 font-medium leading-relaxed italic uppercase tracking-tighter">
                      IMPORTANT: If you lose access to your authenticator app, these codes are the ONLY way to recover your account. Store them securely.
                    </p>
                  </div>
                </div>

                <button 
                  onClick={() => {
                    setMfaSetupStep('idle');
                    setBackupCodes([]);
                  }}
                  className="w-full h-16 bg-cyan-600 text-white font-black text-xs rounded-2xl hover:bg-cyan-500 transition-all uppercase tracking-[0.3em] shadow-xl shadow-cyan-500/20"
                >
                  Return to Settings
                </button>
              </div>
            )}
          </section>

          {/* Membership Section */}
          <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5 overflow-hidden">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 bg-cyan-600 rounded-2xl flex items-center justify-center border border-cyan-500/10 shadow-xl shadow-cyan-500/10">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold font-display text-zinc-900 uppercase tracking-tighter">Membership & Plan</h3>
            </div>

            <div className="bg-zinc-50 rounded-[2.5rem] border border-cyan-500/5 p-8 mb-8">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-xl font-bold text-zinc-900 uppercase tracking-tighter">
                      {planInfo.name}
                    </h4>
                    {isBeta ? (
                      <Badge className="bg-emerald-500 text-white border-none">Beta Access</Badge>
                    ) : isTrial ? (
                      <Badge className="bg-amber-500 text-white border-none">Trial Access</Badge>
                    ) : (
                      <Badge className={`${planInfo.color} border-none`}>
                        {subscription?.status === 'cancelling' || subscription?.status === 'cancelled' ? 'Cancelling' : 'Active'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-cyan-600 font-mono text-[10px] uppercase tracking-[0.2em]">
                      {isBeta ? 'Free (Beta)' : isTrial ? `Trial expires in ${Math.ceil(accessRemaining / 86400)} days` : planInfo.price}
                    </p>
                    {subscription?.billing_provider && (
                      <Badge variant="outline" className="bg-white/50 text-[8px] font-black uppercase tracking-widest px-2 py-0">
                        Via {subscription.billing_provider}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3 w-full md:w-auto">
                  {(isPro || isElite) ? (
                    <>
                      <button 
                        onClick={() => window.location.href = '/plans'}
                        className="px-6 py-4 bg-cyan-600 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-xl hover:bg-cyan-500 transition-all flex items-center gap-2"
                      >
                        <Zap className="w-4 h-4" /> Change Plan
                      </button>
                      
                      {subscription?.billing_provider === 'paddle' && (
                        <button 
                          onClick={() => {
                            if (window.Paddle) {
                              window.Paddle.CustomerPortal.open({
                                customerId: (subscription as any).paddle_customer_id,
                              });
                            }
                          }}
                          className="px-6 py-4 border border-cyan-500/10 text-cyan-600 font-black text-[10px] uppercase tracking-[0.2em] rounded-xl hover:bg-cyan-50 transition-all flex items-center gap-2"
                        >
                          <CreditCard className="w-4 h-4" /> Billing Portal
                        </button>
                      )}

                      {subscription?.status !== 'cancelling' && subscription?.status !== 'cancelled' ? (
                        <button 
                          onClick={handleCancelSubscription}
                          disabled={cancelLoading}
                          className="px-6 py-4 border border-red-100 text-red-500 font-black text-[10px] uppercase tracking-[0.2em] rounded-xl hover:bg-red-50 transition-all flex items-center gap-2"
                        >
                          {cancelLoading ? 'Processing...' : <><XCircle className="w-4 h-4" /> Cancel Plan</>}
                        </button>
                      ) : (
                        subscription?.billing_provider === 'paypal' && (
                          <button 
                            onClick={handleReactivateSubscription}
                            disabled={reactivateLoading}
                            className="px-6 py-4 bg-emerald-500 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-xl hover:bg-emerald-600 transition-all flex items-center gap-2"
                          >
                            {reactivateLoading ? 'Processing...' : <><History className="w-4 h-4" /> Restart Plan</>}
                          </button>
                        )
                      )}
                    </>
                  ) : (
                    <button 
                      onClick={() => window.location.href = '/plans'}
                      className="px-6 py-4 bg-cyan-600 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-xl hover:bg-cyan-500 transition-all flex items-center gap-2"
                    >
                      <Zap className="w-4 h-4" /> Upgrade Plan
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 border border-cyan-500/5 rounded-3xl bg-white">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="w-5 h-5 text-cyan-600/50" />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Billing Policy</span>
                </div>
                <p className="text-zinc-600 text-[11px] leading-tight font-medium uppercase tracking-tight">
                  {isPro || isElite 
                    ? `Your subscription remains valid until ${subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'the end of period'}.`
                    : 'Subscribe to a paid plan to unlock commercial rights and remove watermarks.'}
                </p>
              </div>
              <div className="p-6 border border-cyan-500/5 rounded-3xl bg-white focus-within:ring-2 focus-within:ring-cyan-500/5 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <ArrowUpCircle className="w-5 h-5 text-cyan-600/50" />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Plan Status</span>
                </div>
                <p className="text-zinc-600 text-[11px] leading-tight font-medium uppercase tracking-tight">
                  {subscription?.status === 'cancelling' 
                    ? 'Subscription is set to expire. Access will revert to FREE plan auto-magically.' 
                    : `Account is in good standing. ${isEnterprise ? 'You have active Enterprise Studio privileges.' : isElite ? 'You have active Studio Elite privileges.' : isPro ? 'You have active Pro Creator privileges.' : 'Check your plan status below.'}`}
                </p>
              </div>
            </div>
          </section>

          {/* Beta Program Status Section */}
          <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 bg-cyan-600 rounded-2xl flex items-center justify-center border border-cyan-500/10 shadow-xl shadow-cyan-500/10">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold font-display text-zinc-900 uppercase tracking-tighter">Beta Program</h3>
            </div>

            {user.is_beta ? (
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-emerald-50/50 border border-emerald-500/10 rounded-3xl">
                  <div className="shrink-0 mt-1">
                    <ShieldCheck className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black uppercase tracking-widest text-emerald-800 mb-1 flex items-center gap-2">
                      Active Beta Tester
                    </h4>
                    <p className="text-[11px] text-emerald-700/80 font-mono uppercase tracking-tight">
                      Approved by: {user.beta_granted_by || 'System'}
                    </p>
                    {user.beta_granted_at && (
                      <p className="text-[9px] text-zinc-400 font-mono mt-0.5">
                        Granted on: {new Date(user.beta_granted_at * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>

                <div className="p-6 border border-zinc-100 rounded-3xl bg-zinc-50/30">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 block mb-2">Access Expiration</span>
                  <p className="text-zinc-800 text-sm font-bold uppercase tracking-tight">
                    {user.beta_expires_at ? (
                      <>
                        Expires on {new Date(user.beta_expires_at * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-mono rounded-full lowercase">
                          ({Math.max(0, Math.ceil((user.beta_expires_at - Date.now() / 1000) / 86400))} days remaining)
                        </span>
                      </>
                    ) : (
                      'Lifetime Beta Access (No Expiry)'
                    )}
                  </p>
                </div>
              </div>
            ) : user.access_mode === 'beta_tester' ? (
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-zinc-50 border border-dashed border-zinc-200 rounded-3xl">
                  <div className="shrink-0 mt-1">
                    <XCircle className="w-6 h-6 text-zinc-400" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black uppercase tracking-widest text-zinc-500 mb-1">
                      Beta Access Expired
                    </h4>
                    <p className="text-[11px] text-zinc-400 font-medium leading-relaxed uppercase tracking-tight">
                      Your time-limited beta privileges have expired. Your account has automatically reverted to its base plan.
                    </p>
                  </div>
                </div>

                <div className="p-6 border border-zinc-100 rounded-3xl bg-zinc-50/50">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 block mb-2">Renewal Instructions</span>
                  <p className="text-zinc-500 text-xs font-mono uppercase tracking-[0.1em] leading-relaxed">
                    To request extension of your beta access, please contact your administrator or reach out to support at <span className="text-cyan-600 font-bold">support@funcirclegames.com</span>.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 bg-zinc-50/50 border border-dashed border-zinc-200 rounded-3xl">
                <p className="text-zinc-400 text-xs font-black uppercase tracking-[0.2em]">Not enrolled in Beta Program</p>
                <p className="text-zinc-300 text-[10px] uppercase font-mono tracking-wider mt-2">
                  Beta tester enrollment is invite-only by system administrators.
                </p>
              </div>
            )}
          </section>

          {/* Cloud Storage Usage Section */}
          <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
            <CloudStorageSection />
          </section>

          {/* Transaction History */}
          <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 bg-cyan-600 rounded-2xl flex items-center justify-center border border-cyan-500/10 shadow-xl shadow-cyan-500/10">
                <History className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold font-display text-zinc-900 uppercase tracking-tighter">Purchase History</h3>
            </div>

            {historyLoading ? (
               <div className="flex items-center justify-center py-20">
                 <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-cyan-500"></div>
               </div>
            ) : purchaseHistory.length === 0 ? (
               <div className="text-center py-20 bg-zinc-50 rounded-[3rem] border border-dashed border-zinc-200">
                 <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <History className="w-8 h-8 text-zinc-300" />
                 </div>
                 <p className="text-zinc-400 text-xs font-bold uppercase tracking-[0.2em]">No history records yet</p>
               </div>
            ) : (
              <div className="space-y-4">
                {purchaseHistory.map((item: any) => {
                  const isPayment = item.status === 'PAYMENT_SUCCESSFUL' || item.status === 'COMPLETED';
                  const isActivation = item.status === 'SUBSCRIPTION_ACTIVATED';
                  const isCancelled = item.status === 'SUBSCRIPTION_CANCELLED';
                  
                  return (
                    <div key={item.id} className={`p-8 border rounded-[2rem] transition-all duration-300 ${isPayment ? 'bg-white border-cyan-500/5 shadow-lg shadow-cyan-500/[0.02] hover:shadow-xl hover:shadow-cyan-500/[0.05]' : 'bg-zinc-50/50 border-dashed border-zinc-200'}`}>
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-black uppercase tracking-tighter text-zinc-900">
                              {item.plan_id === 'studio_elite' ? 'Studio Elite' : (item.plan_id === 'creator_pro' ? 'Pro Creator' : 'Custom Plan')}
                            </span>
                            <Badge 
                              className={`text-[9px] py-1 px-3 border-none font-black uppercase tracking-widest rounded-full ${
                                isPayment ? 'bg-cyan-500 text-white' :
                                isActivation ? 'bg-emerald-100 text-emerald-600' :
                                isCancelled ? 'bg-red-100 text-red-600' :
                                'bg-zinc-100 text-zinc-500'
                              }`}
                            >
                              {isPayment ? 'Paid' : item.status.replace('SUBSCRIPTION_', '')}
                            </Badge>
                          </div>
                          <div className="flex flex-col gap-1">
                            <p className="text-[10px] font-mono text-cyan-600/50 uppercase tracking-wider flex items-center gap-2">
                              {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            {item.paypal_order_id && (
                              <p className="text-[9px] font-mono text-zinc-300 uppercase truncate max-w-[200px]">PP ID: {item.paypal_order_id}</p>
                            )}
                            {item.paddle_transaction_id && (
                              <p className="text-[9px] font-mono text-zinc-300 uppercase truncate max-w-[200px]">PD ID: {item.paddle_transaction_id}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between md:justify-end gap-8 w-full md:w-auto">
                          <div className="text-right">
                            <p className={`text-xl font-black ${isPayment ? 'text-zinc-900' : 'text-zinc-400'}`}>
                              {isPayment ? `$${item.amount}` : '-'} <span className="text-[10px] font-bold text-zinc-400">{item.currency}</span>
                            </p>
                            <p className="text-[9px] font-black uppercase tracking-widest text-cyan-600/50 mt-0.5">
                              {isPayment ? 'Payment Confirmed' : isActivation ? 'Status: Activated' : 'Status: Revoked'}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {isPayment && (
                              <a 
                                href={`/api/payments/invoice/${item.id}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-12 h-12 bg-cyan-600 text-white hover:bg-cyan-500 rounded-2xl flex items-center justify-center transition-all shadow-lg shadow-cyan-500/10 group/btn"
                                title="Download Receipt"
                              >
                                <Download className="w-5 h-5 group-hover/btn:scale-110 transition-transform" />
                              </a>
                            )}
                            {(item.paypal_order_id || item.paddle_transaction_id) && (
                              <a 
                                href={item.paypal_order_id ? `https://www.paypal.com/activity/payment/${item.paypal_order_id}` : `https://checkout.paddle.com/receipt/${item.paddle_transaction_id}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-12 h-12 border border-cyan-500/10 rounded-2xl flex items-center justify-center transition-all text-zinc-400 hover:text-cyan-600 hover:bg-cyan-50"
                                title={item.paypal_order_id ? "PayPal Details" : "Paddle Receipt"}
                              >
                                <ExternalLink className="w-5 h-5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Account Actions */}
          <section className="bg-white p-10 md:p-12 rounded-[3.5rem] border border-cyan-500/5 shadow-2xl shadow-cyan-500/5">
             <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 bg-cyan-600 rounded-2xl flex items-center justify-center border border-cyan-500/10 shadow-xl shadow-cyan-500/10">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold font-display text-zinc-900 uppercase tracking-tighter">App Settings</h3>
            </div>

             <div className="space-y-6">
               {isInstallable && (
                 <div className="p-8 bg-zinc-50 border border-cyan-500/5 rounded-[2.5rem] group transition-all hover:bg-white hover:border-cyan-500/20">
                   <div className="flex flex-col md:flex-row items-center md:items-start gap-8 text-center md:text-left">
                     <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-xl shadow-cyan-500/10">
                       <Download className="w-7 h-7" />
                     </div>
                     <div className="space-y-3">
                       <h4 className="text-zinc-900 font-bold uppercase tracking-tighter text-xl">Install App</h4>
                       <p className="text-zinc-600 text-sm font-medium leading-relaxed max-w-sm">
                         Install Fun Circle Games on your device for a better experience.
                       </p>
                       <div className="flex items-center justify-center md:justify-start gap-4 pt-2">
                         <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-cyan-600/50">
                           <Smartphone className="w-3 h-3" /> Device
                         </div>
                         <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-cyan-600/50">
                           <Monitor className="w-3 h-3" /> Desktop
                         </div>
                       </div>
                       <button 
                         onClick={installApp}
                         className="mt-6 px-10 py-4 bg-cyan-600 text-white font-black text-[10px] uppercase tracking-[0.3em] rounded-xl hover:bg-cyan-500 transition-all shadow-xl shadow-cyan-500/20"
                       >
                         Install Now
                       </button>
                     </div>
                   </div>
                 </div>
               )}
               <button 
                onClick={logout} 
                className="w-full h-16 border border-red-100 text-red-500 font-black text-xs rounded-2xl hover:bg-red-500 hover:text-white transition-all uppercase tracking-[0.4em] flex items-center justify-center gap-3 shadow-lg shadow-red-500/5"
               >
                 <LogOut className="w-4 h-4" /> Logout
               </button>
             </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const PasswordField = ({ label, value, show, onChange, toggleShow }: { label: string, value: string, show: boolean, onChange: (v: string) => void, toggleShow: () => void }) => (
  <div className="space-y-3">
    <label className="text-[10px] font-black text-zinc-400 mb-2 uppercase tracking-[0.3em] ml-1">{label}</label>
    <div className="relative group/input">
      <Lock className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 group-focus-within/input:text-cyan-500 transition-colors" />
      <input 
        type={show ? "text" : "password"} 
        value={value} 
        onChange={(e) => onChange(e.target.value)} 
        required 
        className="block w-full pl-14 pr-14 py-5 bg-zinc-50 border border-cyan-500/5 rounded-2xl text-zinc-900 focus:outline-none focus:ring-4 focus:ring-cyan-500/5 focus:border-cyan-500/20 transition-all font-bold placeholder:text-zinc-300 shadow-sm" 
        placeholder="••••••••" 
      />
      <button 
        type="button" 
        onClick={toggleShow} 
        className="absolute right-5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-cyan-600 transition-colors"
      >
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  </div>
);

export default Settings;
